import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse} from '@angular/common/http';
import {environment} from '../../../../environments/environment';
import {catchError, Observable, shareReplay, tap, throwError} from 'rxjs';
import {Occupation} from './occupation.interface';
import {DepartamentPost} from '../../departamentos/services/departament.interface';

@Injectable({
  providedIn: 'root'
})
export class OccupationsService {
  public http = inject(HttpClient);
  private apiUrl = `${environment.apiUrl}/clients`;
  private path = '/departments';

  // Cache por cliente y departamento
  private cache = new Map<string, Observable<Occupation[]>>();

  getOccupations(idClient: string, idDepartment: string): Observable<Occupation[]> {
    if (!idClient || !idDepartment) return throwError(() => new Error('Client ID and Department ID are required'));

    const cacheKey = `${idClient}-${idDepartment}`;
    const cached = this.cache.get(cacheKey);
    if (cached) return cached;

    const req$ = this.http.get<Occupation[]>(`${this.apiUrl}/${idClient}${this.path}/${idDepartment}/occupations`)
      .pipe(
        catchError(this.handleError),
        shareReplay(1)
      );

    this.cache.set(cacheKey, req$);
    return req$;
  }

  // Método para invalidar cache cuando se crean/actualizan/eliminen ocupaciones
  invalidateCache(idClient: string, idDepartment: string): void {
    const cacheKey = `${idClient}-${idDepartment}`;
    this.cache.delete(cacheKey);
  }

  // Invalidar todo el cache de un cliente (útil cuando se elimina un departamento)
  invalidateClientCache(idClient: string): void {
    for (const key of this.cache.keys()) {
      if (key.startsWith(`${idClient}-`)) {
        this.cache.delete(key);
      }
    }
  }

  getOccupation(id: number, idClient: string, idDepartment: string): Observable<Occupation> {
    return this.http.get<Occupation>(`${this.apiUrl}/${idClient}${this.path}/${idDepartment}/occupations/${id}`)
      .pipe(catchError(this.handleError));
  }

  createOccupation(idClient: string, idDepartment: string, departament: DepartamentPost): Observable<Occupation> {
    return this.http.post<Occupation>(`${this.apiUrl}/${idClient}${this.path}/${idDepartment}/occupations`, departament)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient, idDepartment)) // Invalidar cache después de crear
      );
  }

  updateOccupation(id: string, departament: Occupation, idClient: string, idDepartment: string): Observable<Occupation> {
    return this.http.put<Occupation>(`${this.apiUrl}/${idClient}${this.path}/${idDepartment}/occupations/${id}`, departament)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient, idDepartment)) // Invalidar cache después de actualizar
      );
  }

  deleteOccupation(id: number, idClient: string, idDepartment: string): Observable<void> {
    return this.http.delete<void>(`${this.apiUrl}/${idClient}${this.path}/${idDepartment}/occupations/${id}`)
      .pipe(
        catchError(this.handleError),
        tap(() => this.invalidateCache(idClient, idDepartment)) // Invalidar cache después de eliminar
      );
  }

  createOccupationsBatch(clientId: string, idDepartment: string, items: { name: string }[]) {
    return this.http.post<Occupation[]>(
      `${this.apiUrl}/${clientId}${this.path}/${idDepartment}/occupations/batch`,
      items
    ).pipe(
      tap(() => this.invalidateCache(clientId, idDepartment)) // Invalidar cache después de crear batch
    );
  }

  private handleError(error: HttpErrorResponse): Observable<never> {
    let errorMsg = 'Ocurrió un error inesperado';
    if (error.error && error.error.message) {
      errorMsg = error.error.message;
    }
    return throwError(() => new Error(errorMsg));
  }
}
